import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  FileText, 
  Loader, 
  QrCode,
  Printer, 
  Share2, 
  Calendar, 
  User, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Hash,
  MessageSquare
} from 'lucide-react';
import QRCode from 'react-qr-code';
import CaseHistory from '../components/CaseHistory';
import useApi from '../hooks/useApi';
import Button from '../components/ui/Button';
import { useSettings } from '../context/SettingsContext';

const DigitalGreenSheet = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get } = useApi(); 
  const { settings } = useSettings();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const [receipt, setReceipt] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [activeAttachment, setActiveAttachment] = useState(null);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    if (!isEfileEnabled) {
      setLoading(false);
      return;
    }
    fetchReceipt();
  }, [id, isEfileEnabled]);

  useEffect(() => {
    if (activeAttachment) {
      fetchPdf(activeAttachment);
    }
  }, [activeAttachment]);

  const fetchReceipt = async () => {
    try {
      setLoading(true);
      const response = await get(`/receipt/${id}`);
      if (response.data) {
        setReceipt(response.data);
        // Find first PDF attachment
        const pdf = response.data.attachments?.find(f => f.mimetype === 'application/pdf');
        if (pdf) {
          setActiveAttachment(pdf);
        }
      }
    } catch (error) {
      console.error('Failed to fetch receipt', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPdf = async (attachment) => {
    try {
      setLoadingPdf(true);
      const token = localStorage.getItem('token');
      // Fetch file as blob
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/files/${attachment.filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch PDF');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Failed to load PDF', error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDownloadGreenSheet = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use the green sheet endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/pdf/green-sheet/receipt/${id}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to generate Green Sheet');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GreenSheet_${receipt.diaryNumber.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed', error);
      alert('Failed to download Green Sheet');
    }
  };

  if (!isEfileEnabled) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 max-w-md w-full">
          <div className="text-lg font-semibold text-gray-900">Green Sheet is disabled</div>
          <div className="text-sm text-gray-600 mt-2">
            Enable the e-File module to use Green Sheet features.
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={() => navigate(-1)} variant="secondary">Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-full">
          <Loader className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  if (!receipt) {
    return (
      <>
        <div className="text-center py-10">
          <h2 className="text-xl font-semibold text-gray-700">Receipt Not Found</h2>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col h-[calc(100vh-100px)]">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm z-10">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Digital Green Sheet
              </h1>
              <p className="text-xs text-gray-500">
                {receipt.diaryNumber} • {receipt.subject.substring(0, 50)}...
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowQr(true)}
              icon={QrCode}
              variant="outline"
              className="border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              QR Code
            </Button>
            <Button 
              onClick={handleDownloadGreenSheet} 
              icon={Download} 
              variant="primary"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Download Green Sheet
            </Button>
          </div>
        </div>

        {/* Split View Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Pane: Correspondence (PDF) */}
          <div className="w-1/2 border-r border-gray-200 bg-gray-100 flex flex-col">
            <div className="px-4 py-2 bg-white border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider flex justify-between items-center">
              <span>Correspondence (Left Side)</span>
              {activeAttachment && (
                <span className="text-gray-400 font-normal truncate max-w-[200px]" title={activeAttachment.originalname}>
                  {activeAttachment.originalname}
                </span>
              )}
            </div>
            <div className="flex-1 overflow-hidden relative">
              {loadingPdf ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : pdfUrl ? (
                <iframe 
                  src={pdfUrl} 
                  className="w-full h-full border-0" 
                  title="Correspondence Viewer"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8 text-center">
                  <FileText className="w-16 h-16 mb-4 opacity-20" />
                  <p>No PDF document available to view.</p>
                  <p className="text-xs mt-2">Upload a PDF attachment to see it here.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Pane: Notes (Green Sheet) */}
          <div className="w-1/2 bg-white flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-xs font-semibold text-green-800 uppercase tracking-wider">
              Notes Sheet (Right Side)
            </div>
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
               {/* Reusing CaseHistory but styled for Green Sheet view */}
               <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 min-h-full">
                 <CaseHistory caseId={id} type="receipt" />
               </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowQr(false)}>
          <div className="bg-white p-6 rounded-xl shadow-2xl max-w-sm w-full transform transition-all" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Case QR Code</h3>
                <p className="text-xs text-gray-500">Scan to track physical file</p>
              </div>
              <button 
                onClick={() => setShowQr(false)} 
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex flex-col items-center justify-center p-6 bg-white border-2 border-dashed border-gray-200 rounded-xl mb-4">
              <div className="bg-white p-2">
                <QRCode value={`DAK-CASE:${id}`} size={200} level="H" />
              </div>
              <p className="text-sm font-mono font-bold text-gray-800 mt-4 tracking-wider">{receipt.diaryNumber}</p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button onClick={() => setShowQr(false)} variant="secondary">Close</Button>
              <Button onClick={() => window.print()} variant="primary">Print Label</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DigitalGreenSheet;
